/**
 * useUserRole Hook
 * 
 * Fetches and caches the current user's role in the selected company
 * Uses React Query for efficient caching and automatic refetching
 * 
 * @example
 * ```tsx
 * const { role, isOwner, isAdmin, isEditor, isViewer, loading } = useUserRole();
 * 
 * if (isOwner) {
 *   // Show owner-only features
 * }
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCompanyContext } from '../contexts/CompanyContext';
import { useAuth } from '../hooks/useAuth';
import type { MemberRole } from '../types/sharing';

interface UserRoleData {
  role: MemberRole | null;
  loading: boolean;
  error: Error | null;
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
}

/**
 * Hook to get the current user's role in the selected company
 */
export function useUserRole(): UserRoleData {
  const { user } = useAuth();
  const { selectedCompanyIds } = useCompanyContext();
  
  // Get the first selected company (single company mode for permissions)
  const companyId = selectedCompanyIds[0];

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-role', user?.id, companyId],
    queryFn: async () => {
      if (!user?.id || !companyId) {
        return null;
      }

      // First, check if user is the company owner
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('user_id')
        .eq('id', companyId)
        .single();

      if (companyError) {
        console.error('[useUserRole] Error fetching company:', companyError);
        throw companyError;
      }

      // If user is the company owner, return 'owner' role
      if (company?.user_id === user.id) {
        return 'owner' as MemberRole;
      }

      // Otherwise, check company_users table for their role
      const { data: membership, error: membershipError } = await supabase
        .from('company_users')
        .select('role')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .not('accepted_at', 'is', null)
        .maybeSingle();

      if (membershipError) {
        console.error('[useUserRole] Error fetching membership:', membershipError);
        throw membershipError;
      }

      return membership?.role as MemberRole || null;
    },
    enabled: !!user?.id && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  const role = data || null;

  return {
    role,
    loading: isLoading,
    error: error as Error | null,
    isOwner: role === 'owner',
    isAdmin: role === 'admin',
    isEditor: role === 'editor',
    isViewer: role === 'viewer',
  };
}

